import {
  AllowedContractActionsList as AllowedContractActionsDto,
  BillingCycle,
  BillingTransaction as BillingTransactionDto,
  Contract as ContractDto,
  ContractsList as ContractsListDto,
  CurrencyType,
  CustomerInfo as CustomerDto,
  EstimatedQuote as EstimatedQuoteDto,
  InvoiceStatus,
  ItemizedContract as ItemizedContractDto,
  ItemizedQuote as ItemizedQuoteDto,
  Quote as QuoteDto,
  QuotePurpose,
  QuoteStatus,
  SubscriptionScheduleStatus,
  TransactionSourceType,
  TransactionType,
} from '@electricjs/core_entity-client';

import { mockProductsListWithOnlyAllowed } from '@/pages/Internal/Billing/Quotes/__mocks__/mockProductsData';

export const salesProducts = mockProductsListWithOnlyAllowed;

const packagePrice = salesProducts.packages[0].pricing.month_price;

export const salesCustomers: CustomerDto[] = [
  {
    customer_id: 'sales-customer-1',
    name: 'Simple Org',
    organization_id: '67d26876-05a6-11ee-be56-0242ac120002',
    billing_address: {
      line1: '915 Broadway',
      line2: 'Suite 802',
      city: 'New York',
      region: 'NY',
      country_code: 'US',
      postal_code: '10010',
    },
    billing_email: 'billing@example.com',
    created_at: '2024-01-10T12:00:00Z',
  },
  {
    customer_id: 'sales-customer-2',
    name: 'Acme Corp',
    billing_address: {
      line1: '1 Market St',
      city: 'San Francisco',
      region: 'CA',
      country_code: 'US',
      postal_code: '94105',
    },
    billing_email: 'accounts@acme.example.com',
    created_at: '2024-03-02T12:00:00Z',
  },
];

const quoteBase = {
  currency: CurrencyType.Usd,
  customer: salesCustomers[0],
  purpose: QuotePurpose.NewBusiness,
  created_by_user_id: 'simple-user-id',
  created_at: '2024-05-01T12:00:00Z',
  expires_at: '2030-06-01T00:00:00Z',
  payment_terms: { collection_method: 'charge_automatically' },
  contract_terms: {
    start_date: '2024-06-01',
    contract_length_months: 12,
    billing_cycle: BillingCycle.Month,
  },
  effective_date: '2024-06-01',
  salesforce_opportunity_id: '006XX000001234AAA',
  billing_contact: { name: 'Jane Billing', email: 'billing@example.com' },
  source: 'internal',
} as const;

export const salesQuotes: QuoteDto[] = [
  { ...quoteBase, quote_id: 'sales-quote-1', status: QuoteStatus.Draft },
  { ...quoteBase, quote_id: 'sales-quote-2', status: QuoteStatus.Finalized },
  { ...quoteBase, quote_id: 'sales-quote-3', status: QuoteStatus.Accepted },
];

const usd = (value: string) => ({ value, currency: CurrencyType.Usd });

export const itemizedSalesQuote: ItemizedQuoteDto = {
  ...salesQuotes[0],
  line_items: [
    {
      amount_total: usd('1200.00'),
      amount_subtotal: usd('1200.00'),
      amount_tax: usd('0.00'),
      amount_discount: usd('0.00'),
      price: packagePrice,
      quantity: 10,
    },
  ],
  amount_term_subtotal: usd('14400.00'),
  amount_term_discount: usd('0.00'),
  amount_term_total: usd('14400.00'),
};

export const estimatedSalesQuote: EstimatedQuoteDto = {
  currency: CurrencyType.Usd,
  customer: salesCustomers[0],
  expires_at: quoteBase.expires_at,
  payment_terms: quoteBase.payment_terms,
  contract_terms: quoteBase.contract_terms,
  line_items: [
    {
      amount_total: usd('1200.00'),
      amount_subtotal: usd('1200.00'),
      amount_discount: usd('0.00'),
      price: packagePrice,
      quantity: 10,
    },
  ],
  amount_term_subtotal: usd('14400.00'),
  amount_term_discount: usd('0.00'),
  amount_term_total: usd('14400.00'),
};

export const salesContracts: ContractDto[] = [
  {
    contract_id: 'sales-contract-1',
    customer: salesCustomers[0],
    start_date: '2024-06-01',
    end_date: '2025-06-01',
    created_at: '2024-05-15T12:00:00Z',
    created_by_user_id: 'simple-user-id',
    status: SubscriptionScheduleStatus.Active,
    subscription_schedule_id: 'sub-sched-1',
    subscription_id: 'sub-1',
    quote_id: 'sales-quote-3',
  },
];

export const salesContractsList: ContractsListDto = {
  contracts: salesContracts,
};

export const itemizedSalesContract: ItemizedContractDto = {
  ...salesContracts[0],
  current_phase: {
    start_date: '2024-06-01',
    end_date: '2025-06-01',
    quote_id: 'sales-quote-3',
    created_by_user_id: 'simple-user-id',
    items: [{ quantity: 10, price: packagePrice }],
  },
  past_phases: [],
  pending_phases: [],
};

export const allowedContractActions: AllowedContractActionsDto = {
  actions: [QuotePurpose.Amendment, QuotePurpose.EarlyRenewal],
};

export const adhocInvoiceTransaction: BillingTransactionDto = {
  initial_invoice: {
    invoice_id: 'adhoc-invoice-1',
    organization_id: '67d26876-05a6-11ee-be56-0242ac120002',
    invoice_number: 'INV-ADHOC-1',
    transaction_type: TransactionType.AdhocInvoice,
    source_type: TransactionSourceType.Unknown,
    status: InvoiceStatus.Open,
    description: 'Ad hoc invoice',
    hosted_invoice_url: 'https://example.com/invoice/adhoc-1',
    pdf_url: 'https://example.com/invoice/adhoc-1.pdf',
    due_date: '2030-01-01T00:00:00Z',
    amount_paid: 0,
    amount_total: 1000,
    created_at: '2024-05-01T12:00:00Z',
    is_overdue: false,
  },
  amount_total: 1000,
};
